import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  PenTool, 
  Lightbulb, 
  StickyNote,
  Plus,
  X,
  ChevronDown,
  ChevronRight,
  Clock,
  CheckCircle,
  AlertCircle,
  Link2,
  Target,
  Sparkles,
  Edit3,
  Brain,
  Heart,
  Building,
  Users
} from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { toast } from 'react-hot-toast';
import { supabase } from '../../../lib/supabase';
import EssaySlotService from '../../../services/EssaySlotService';

const EssaySlotCard = ({ 
  slot, 
  onSlotUpdate,
  onSlotClick,
  onAssignContent,
  onRemoveContent,
  college,
  deadline
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [primaryDraft, setPrimaryDraft] = useState(null);
  const [attachedFreewrites, setAttachedFreewrites] = useState([]);
  const [attachedIdeas, setAttachedIdeas] = useState([]);
  const [collegeInsights, setCollegeInsights] = useState(slot?.college_insights || {});
  const [isUpdating, setIsUpdating] = useState(false);

  // Early return if no slot
  if (!slot) {
    return (
      <div className="bg-white rounded-lg border-2 border-stone-200 p-4">
        <p className="text-stone-500">No slot data available</p>
      </div>
    );
  }

  // Load attached content details
  useEffect(() => {
    loadAttachedContent();
  }, [slot]);

  const loadAttachedContent = async () => {
    if (!slot) return;
    
    try {
      // Load primary draft
      if (slot.assigned_essay_id) {
        const { data: essay } = await supabase
          .from('college_journey_essays')
          .select('*')
          .eq('id', slot.assigned_essay_id)
          .single();
        setPrimaryDraft(essay);
      }

      // Load freewrites
      if (slot.assigned_freewrite_ids && Array.isArray(slot.assigned_freewrite_ids) && slot.assigned_freewrite_ids.length > 0) {
        const { data: freewrites } = await supabase
          .from('college_journey_freewrites')
          .select('*')
          .in('id', slot.assigned_freewrite_ids);
        setAttachedFreewrites(freewrites || []);
      } else {
        setAttachedFreewrites([]);
      }

      // Load ideas
      if (slot.assigned_idea_ids && Array.isArray(slot.assigned_idea_ids) && slot.assigned_idea_ids.length > 0) {
        const { data: ideas } = await supabase
          .from('college_journey_essay_ideas')
          .select('*')
          .in('id', slot.assigned_idea_ids);
        setAttachedIdeas(ideas || []);
      } else {
        setAttachedIdeas([]);
      }
    } catch (error) {
      console.error('Error loading attached content:', error);
    }
  };

  // Note: Drag and drop removed - use modal assignment instead
  // Content can be attached via the "+" buttons which open the assignment modal

  const removeAttachment = async (contentId, type) => {
    try {
      setIsUpdating(true);
      let updateData = {};
      
      if (type === 'essay') {
        updateData.assigned_essay_id = null;
      } else if (type === 'freewrite') {
        updateData.assigned_freewrite_ids = slot.assigned_freewrite_ids.filter(id => id !== contentId);
      } else if (type === 'idea') {
        updateData.assigned_idea_ids = slot.assigned_idea_ids.filter(id => id !== contentId);
      }
      
      // Remove from metadata
      const updatedMetadata = { ...slot.attached_metadata };
      delete updatedMetadata[contentId];
      updateData.attached_metadata = updatedMetadata;
      
      await EssaySlotService.updateSlot(slot.id, updateData);
      if (onSlotUpdate) onSlotUpdate();
      toast.success(`${type} removed`);
    } catch (error) {
      console.error('Error removing attachment:', error);
      toast.error('Failed to remove attachment');
    } finally {
      setIsUpdating(false);
    }
  };

  // Calculate progress
  const getProgress = () => {
    if (!primaryDraft) return 0;
    if (!slot.word_limit) return 0;
    return Math.min(100, Math.round((primaryDraft.word_count / slot.word_limit) * 100));
  };

  // Get status display
  const getStatusDisplay = () => {
    const progress = getProgress();
    if (progress === 0) return { 
      icon: AlertCircle, 
      color: 'text-red-500', 
      label: 'Not Started',
      dotColor: 'bg-red-500',
      bgColor: 'bg-red-50'
    };
    if (progress < 50) return { 
      icon: Clock, 
      color: 'text-orange-500', 
      label: 'In Progress',
      dotColor: 'bg-orange-500',
      bgColor: 'bg-orange-50'
    };
    if (progress < 90) return { 
      icon: Edit3, 
      color: 'text-azure-500', 
      label: 'Drafting',
      dotColor: 'bg-azure-500',
      bgColor: 'bg-azure-50'
    };
    return { 
      icon: CheckCircle, 
      color: 'text-emerald-500', 
      label: 'Near Complete',
      dotColor: 'bg-emerald-500',
      bgColor: 'bg-emerald-50'
    };
  };

  const statusDisplay = getStatusDisplay();
  const StatusIcon = statusDisplay.icon;

  // Calculate days until deadline
  const getDaysUntil = () => {
    if (!deadline) return null;
    return differenceInDays(new Date(deadline), new Date());
  };

  const daysLeft = getDaysUntil();

  return (
    <div 
      className="bg-white rounded-xl border border-stone-200 shadow-sm transition-all duration-300 hover:shadow-lg hover:border-violet-300 overflow-hidden"
    >
      {/* Header */}
      <div className="p-5 border-b border-stone-100">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-2 h-2 rounded-full ${statusDisplay.dotColor}`} />
              <h3 className="text-lg font-semibold text-slate-900">
                {EssaySlotService.getSlotTypeLabel(slot.slot_type)}
              </h3>
              {slot.essay_slot > 1 && (
                <span className="text-xs bg-stone-100 text-slate-600 px-2 py-0.5 rounded-full">
                  #{slot.essay_slot}
                </span>
              )}
            </div>
            
            {/* Prompt and metadata */}
            <div className="space-y-2">
              {slot.prompt_text && (
                <p className="text-sm text-slate-600 line-clamp-2">
                  {slot.prompt_text}
                </p>
              )}
              
              <div className="flex items-center gap-3 text-xs">
                {slot.word_limit && (
                  <span className="text-stone-500">
                    {slot.word_limit} words
                  </span>
                )}
                {daysLeft !== null && (
                  <span className={`${
                    daysLeft < 7 ? 'text-red-600 font-semibold' : 
                    daysLeft < 14 ? 'text-orange-600' : 
                    'text-stone-500'
                  }`}>
                    Due in {daysLeft} days
                  </span>
                )}
                {slot.is_optional && (
                  <span className="text-stone-400 italic">Optional</span>
                )}
              </div>
              
              {/* Progress bar */}
              <div className="h-1 bg-stone-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-500 rounded-full ${
                    getProgress() === 0 ? 'bg-stone-300' :
                    getProgress() < 50 ? 'bg-orange-500' :
                    getProgress() < 90 ? 'bg-azure-500' :
                    'bg-emerald-500'
                  }`}
                  style={{ width: `${getProgress()}%` }}
                />
              </div>
            </div>
          </div>
          
          {/* Action buttons */}
          <div className="flex items-center gap-2 ml-4">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              title={isExpanded ? "Collapse" : "Expand"}
            >
              <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${
                isExpanded ? '' : '-rotate-90'
              }`} />
            </button>
          </div>
        </div>
      </div>

      {/* Primary Draft Section */}
      <div className="p-5">
        {primaryDraft ? (
          <div className="bg-gradient-to-r from-violet-50 to-azure-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <FileText className="w-4 h-4 text-violet-600" />
                Primary Draft
              </h4>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    // Use the onSlotClick handler to navigate to the essay with attached content
                    if (onSlotClick) {
                      onSlotClick({
                        ...slot, 
                        assigned_essay_id: primaryDraft.id,
                        attachedFreewrites,
                        attachedIdeas,
                        collegeInsights
                      });
                    }
                  }}
                  className="p-2 text-azure-600 hover:bg-white rounded-lg transition-colors"
                  title="Edit Essay"
                >
                  <Edit3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => removeAttachment(primaryDraft.id, 'essay')}
                  className="p-2 text-red-600 hover:bg-white rounded-lg transition-colors"
                  title="Remove"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="bg-white rounded-lg p-3">
              <p className="font-medium text-slate-900 mb-1">
                {primaryDraft.title || 'Untitled Essay'}
              </p>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">
                  {primaryDraft.word_count || 0} / {slot.word_limit || '?'} words
                </span>
                <span className={`font-medium ${
                  getProgress() >= 90 ? 'text-emerald-600' :
                  getProgress() >= 50 ? 'text-azure-600' :
                  'text-orange-600'
                }`}>
                  {getProgress()}% complete
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="border-2 border-dashed border-stone-300 rounded-lg p-6 text-center bg-stone-50 hover:bg-stone-100 hover:border-violet-400 transition-all">
            <FileText className="w-8 h-8 text-stone-400 mx-auto mb-3" />
            <h4 className="text-base font-medium text-slate-700 mb-1">
              No Essay Draft Assigned Yet
            </h4>
            <p className="text-sm text-stone-500 mb-3">
              Start writing or attach an existing draft to begin
            </p>
            <button
              onClick={() => onAssignContent?.('essay', slot.id)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              Attach Draft
            </button>
          </div>
        )}
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-5 pb-5">
          {/* Supporting Content Grid */}
          <div className="grid grid-cols-3 gap-4">
            {/* Freewrites Section */}
            <div className="bg-stone-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-semibold text-slate-700 flex items-center gap-2">
                <span className="bg-white text-slate-600 px-2 py-0.5 rounded-full text-xs">
                    {attachedFreewrites.length}
                  </span>
                  Freewrites
                  
                </h4>
                <button
                  onClick={() => onAssignContent?.('freewrite', slot.id)}
                  className="text-xs text-violet-600 hover:text-violet-700 flex items-center gap-1 font-medium"
                >
                  <Plus className="w-3 h-3" />
                  Add
                </button>
              </div>
              {attachedFreewrites.length > 0 ? (
                <div className="space-y-2">
                  {attachedFreewrites.map(fw => (
                    <div key={fw.id} className="bg-white rounded p-2.5 flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-700">
                          {format(new Date(fw.date), 'MMM d, yyyy')}
                        </p>
                        <p className="text-xs text-slate-600 line-clamp-1">
                          {fw.prompt || fw.content?.substring(0, 50)}...
                        </p>
                      </div>
                      <button
                        onClick={() => removeAttachment(fw.id, 'freewrite')}
                        className="p-1 text-stone-400 hover:text-red-600 ml-2"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-stone-500 text-center py-2">No freewrites attached</p>
              )}
            </div>

            {/* Ideas Section */}
            <div className="bg-stone-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-semibold text-slate-700 flex items-center gap-2">
                <span className="bg-white text-slate-600 px-2 py-0.5 rounded-full text-xs">
                    {attachedIdeas.length}
                  </span>                  Ideas
                  
                </h4>
                <button
                  onClick={() => onAssignContent?.('idea', slot.id)}
                  className="text-xs text-violet-600 hover:text-violet-700 flex items-center gap-1 font-medium"
                >
                  <Plus className="w-3 h-3" />
                  Add
                </button>
              </div>
              {attachedIdeas.length > 0 ? (
                <div className="space-y-2">
                  {attachedIdeas.map(idea => (
                    <div key={idea.id} className="bg-white rounded p-2.5 flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-700 truncate">{idea.title}</p>
                        {idea.tags && (
                          <div className="flex gap-1 mt-1">
                            {idea.tags.slice(0, 2).map(tag => (
                              <span key={tag} className="text-xs px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => removeAttachment(idea.id, 'idea')}
                        className="p-1 text-stone-400 hover:text-red-600 ml-2"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-stone-500 text-center py-2">No ideas attached</p>
              )}
            </div>

            {/* College Strategy Notes */}
            <div className="bg-stone-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-semibold text-slate-700 flex items-center gap-2">
                  <Brain className="w-4 h-4 text-violet-600" />
                  Strategy Notes
                </h4>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {collegeInsights?.strategy_notes?.programs?.value && (
                  <div className="bg-white rounded p-2.5">
                    <div className="flex items-start gap-2">
                      <Building className="w-3 h-3 text-violet-600 mt-1 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-slate-700">Programs & Research</p>
                        <p className="text-xs text-slate-600 line-clamp-2 mt-0.5">
                          {collegeInsights.strategy_notes.programs.value}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {collegeInsights?.strategy_notes?.interests?.value && (
                  <div className="bg-white rounded p-2.5">
                    <div className="flex items-start gap-2">
                      <Heart className="w-3 h-3 text-violet-600 mt-1 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-slate-700">What Excites You</p>
                        <p className="text-xs text-slate-600 line-clamp-2 mt-0.5">
                          {collegeInsights.strategy_notes.interests.value}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {collegeInsights?.strategy_notes?.perspectives?.value && (
                  <div className="bg-white rounded p-2.5">
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-3 h-3 text-violet-600 mt-1 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-slate-700">Your Uniqueness</p>
                        <p className="text-xs text-slate-600 line-clamp-2 mt-0.5">
                          {collegeInsights.strategy_notes.perspectives.value}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {collegeInsights?.strategy_notes?.campus_life?.value && (
                  <div className="bg-white rounded p-2.5">
                    <div className="flex items-start gap-2">
                      <Users className="w-3 h-3 text-violet-600 mt-1 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-slate-700">Campus Life</p>
                        <p className="text-xs text-slate-600 line-clamp-2 mt-0.5">
                          {collegeInsights.strategy_notes.campus_life.value}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {collegeInsights?.strategy_notes?.specific_positioning?.value && (
                  <div className="bg-gradient-to-r from-violet-50 to-amber-50 border border-violet-200 rounded p-2.5">
                    <div className="flex items-start gap-2">
                      <Target className="w-3 h-3 text-violet-600 mt-1 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-violet-900">🎯 Specific Positioning</p>
                        <p className="text-xs font-medium text-slate-700 mt-1">
                          {collegeInsights.strategy_notes.specific_positioning.value}
                        </p>
                        {collegeInsights.strategy_notes.specific_positioning.details?.why && (
                          <p className="text-xs text-slate-600 mt-1">
                            <strong>Why:</strong> {collegeInsights.strategy_notes.specific_positioning.details.why}
                          </p>
                        )}
                        {collegeInsights.strategy_notes.specific_positioning.details?.tactic && (
                          <p className="text-xs text-violet-700 mt-1">
                            💡 <strong>Tactic:</strong> {collegeInsights.strategy_notes.specific_positioning.details.tactic}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {(!collegeInsights?.strategy_notes ||
                  (!collegeInsights.strategy_notes.programs?.value &&
                   !collegeInsights.strategy_notes.interests?.value &&
                   !collegeInsights.strategy_notes.perspectives?.value &&
                   !collegeInsights.strategy_notes.campus_life?.value &&
                   !collegeInsights.strategy_notes.specific_positioning?.value)) && (
                  <p className="text-xs text-stone-500 text-center py-2">No strategy notes yet</p>
                )}
              </div>
            </div>
          </div>

          {/* College Insights */}
          {(collegeInsights.strategic_advantages || college?.student_advantages) && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
                <Target className="w-4 h-4 text-violet-600" />
                College Insights
              </h4>
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-slate-700 leading-relaxed">
                  {collegeInsights.strategic_advantages || college?.student_advantages}
                </p>
                {collegeInsights.why_this_school && (
                  <p className="text-sm text-slate-600 mt-2">
                    <strong className="text-slate-700">Why this school:</strong> {collegeInsights.why_this_school}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Reuse Suggestion */}
          {slot.is_reused_from && (
            <div className="mt-4 flex items-center gap-2 p-3 bg-violet-50 border border-violet-200 rounded-lg">
              <Link2 className="w-4 h-4 text-violet-600" />
              <span className="text-sm text-violet-700">
                This essay can be reused from another slot
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EssaySlotCard;